import DotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import {
  Children,
  createContext,
  memo,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { cn } from "../../tailwind-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// Approximate width of the "..." overflow button (h-8 icon button with px-1.5 padding)
const OVERFLOW_BUTTON_WIDTH = 36;
// gap-1 = 4px
const TOOLBAR_GAP = 4;

export interface ToolbarOverflowContextValue {
  isOverflow: boolean;
}

export const ToolbarOverflowContext =
  createContext<ToolbarOverflowContextValue>({ isOverflow: false });

export function useToolbarOverflow(): ToolbarOverflowContextValue {
  return useContext(ToolbarOverflowContext);
}

export const ToolbarWithOverflow = memo(function ToolbarWithOverflow({
  children,
  className,
  collapseAll,
  onOverflowButtonClick,
  onOverflowButtonRef,
}: {
  children: React.ReactNode;
  className?: string;
  /** When true, all items collapse into the overflow menu together (no partial overflow) */
  collapseAll?: boolean;
  /** When provided, the "..." button calls this instead of toggling the internal dropdown */
  onOverflowButtonClick?: () => void;
  /** When provided, receives the "..." button element (for anchoring an external menu) */
  onOverflowButtonRef?: (el: HTMLButtonElement | null) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cachedWidths = useRef<number[]>([]);
  const [overflowStartIndex, setOverflowStartIndex] = useState<number | null>(
    null,
  );
  const [open, setOpen] = useState(false);

  const childrenArray = Children.toArray(children);

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    if (!container || cachedWidths.current.length === 0) return;

    const containerWidth = container.offsetWidth;
    const widths = cachedWidths.current;

    // Check if all items fit without an overflow button
    const totalWidth =
      widths.reduce((sum, w) => sum + w, 0) +
      TOOLBAR_GAP * Math.max(0, widths.length - 1);

    if (totalWidth <= containerWidth) {
      setOverflowStartIndex(null);
      return;
    }

    // When collapseAll is set, all items go into the overflow menu together
    if (collapseAll) {
      setOverflowStartIndex(0);
      return;
    }

    // Find cutoff, reserving space for the "..." button
    const available = containerWidth - OVERFLOW_BUTTON_WIDTH - TOOLBAR_GAP;

    let running = 0;
    for (const [i, width] of widths.entries()) {
      const itemWidth = (i > 0 ? TOOLBAR_GAP : 0) + width!;
      if (running + itemWidth > available) {
        setOverflowStartIndex(i);
        return;
      }
      running += itemWidth;
    }

    setOverflowStartIndex(null);
  }, [collapseAll]);

  useLayoutEffect(() => {
    // Cache widths on initial render — all items are visible at this point
    cachedWidths.current = itemRefs.current.map((el) => el?.offsetWidth ?? 0);

    recalculate();

    const observer = new ResizeObserver(recalculate);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate, childrenArray.length]);

  const hasOverflow = overflowStartIndex !== null;
  const overflowChildren = hasOverflow
    ? childrenArray.slice(overflowStartIndex)
    : [];

  return (
    <div
      className={cn("flex items-center gap-1 overflow-hidden", className)}
      ref={containerRef}
    >
      {childrenArray.map((child, i) => {
        // Once overflow is calculated, don't render overflow items in the toolbar
        if (hasOverflow && i >= overflowStartIndex!) return null;
        return (
          <div
            key={i}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
          >
            {child}
          </div>
        );
      })}

      {hasOverflow &&
        (onOverflowButtonClick ? (
          <RedoButton
            hierarchy="tertiary"
            IconLeading={DotsHorizontalIcon}
            onClick={onOverflowButtonClick}
            ref={onOverflowButtonRef}
            size="sm"
            text=""
          />
        ) : (
          <DropdownMenu modal={false} onOpenChange={setOpen} open={open}>
            <DropdownMenuTrigger asChild>
              <RedoButton
                hierarchy="tertiary"
                IconLeading={DotsHorizontalIcon}
                size="sm"
                text=""
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="p-1">
              <ToolbarOverflowContext.Provider value={{ isOverflow: true }}>
                <div className="flex flex-col gap-1 [&_svg]:!size-4 [&_svg]:![color:oklch(0.145_0_0)] [&_[data-slot=dropdown-menu-sub-trigger]]:gap-2">
                  {overflowChildren}
                </div>
              </ToolbarOverflowContext.Provider>
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
    </div>
  );
});
